import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { action, observable, reaction } from 'mobx';
import { authorize } from 'utils/auth';
import { FormattedMessage } from 'ui/i18n';
import { INTERNAL_ERROR, BAD_REQUEST } from 'config/constants';
import { getIndex } from 'utils/helpers';
import { getYesNoValue } from 'api/helpers';
import {
	render_amount,
	render_date,
	render_solo_item,
	render_yes_no,
	render_editable_number,
	render_quantity,
	render_holdInvWeeks,
} from 'utils';
import PaginationOptions from 'utils/domain/PaginationOptions';
import SortingOptions from 'utils/domain/SortingOptions';

import {
	InfoBox,
	DataTable,
	Loader,
	ChooseColor,
	ErrorLoadingContent,
	PageHeader,
	ItemContentHeader,
} from 'ui/components';

import './ItemSearch.scss';

function getColumns(toggleModal, onChangeQty, onBlurHandler) {
	return [
		{
			prop: 'itemName',
			title: <FormattedMessage id="itemsTable.soloItemPackNameColumn" />,
			sortable: true,
			render(row) {
				return render_solo_item(row[this.prop], row, toggleModal, false, true);
			},
		},
		{
			prop: 'promotion_name',
			title: <FormattedMessage id="itemsTable.promoNameColumn" />,
			align: 'left',
			sortable: true,
		},
		{
			prop: 'end_date',
			title: <FormattedMessage id="itemsTable.endDateColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(row[this.prop]);
			},
			noWrap: true,
		},
		{
			prop: 'quantity',
			title: <FormattedMessage id="itemsTable.qtyColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_quantity(row[this.prop]);
			},
		},
		{
			prop: 'list_price',
			title: <FormattedMessage id="itemsTable.listColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'promo_price',
			title: <FormattedMessage id="itemsTable.promoColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'ta',
			title: <FormattedMessage id="itemsTable.TAColumn" />,
			sortable: true,
			align: 'right',
			render(row) {
				return `${row[this.prop]}%`;
			},
		},
		{
			prop: 'net_price',
			title: <FormattedMessage id="itemsTable.netColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'est_ship_date',
			title: <FormattedMessage id="itemsTable.estShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(
					row[this.prop],
					<FormattedMessage id="item.See_Components" />
				);
			},
		},
		{
			prop: 'invoice_hold_weeks',
			propToSort: 'displayed_invoice_hold_weeks',
			title: <FormattedMessage id="itemsTable.numberHoldInvWksColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_holdInvWeeks(row, row[this.prop]);
			},
		},
		{
			prop: 'is_drop_ship',
			title: <FormattedMessage id="itemsTable.dropShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_yes_no(getYesNoValue(row[this.prop]));
			},
		},
		{
			prop: 'id',
			title: <FormattedMessage id="itemsTable.addToCartQtyColumn" />,
			align: 'center',
			render(row) {
				return render_editable_number(
					row,
					null,
					false,
					onChangeQty,
					onBlurHandler
				);
			},
		},
	];
}

@authorize({ rights: ['SearchByItemNumber'] })
@inject('SearchState', 'AppState', 'CartState', 'CartItemsState')
@observer
export default class ItemSearchPage extends Component {
	static propTypes = {
		SearchState: PropTypes.object.isRequired,
		AppState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		location: PropTypes.object.isRequired,
	};

	constructor(props) {
		super(props);

		/**
		 * Trigger a search on order change
		 */
		reaction(
			() => [this.pagination, this.order],
			() =>
				this.SearchState.search({
					query: this.SearchState.query,
					pagination: this.pagination,
					sorting: this.order,
				})
		);
	}

	componentWillReceiveProps(nextProps) {
		if (this.props.location.state.query !== nextProps.location.state.query) {
			this.pagination.page = 1;
			this.pagination.size = 15;
		}
	}

	SearchState = this.props.SearchState;
	AppState = this.props.AppState;
	CartState = this.props.CartState;
	CartItemsState = this.props.CartItemsState;
	@observable showModal = false;
	@observable order = new SortingOptions('itemName', 'asc');
	@observable pagination = new PaginationOptions();

	onChangeQty(value, item) {
		if (value) {
			item.quantity_ordered = value;
			item.errorMsgId = this.CartItemsState.getErrorMessageId(
				false,
				this.CartState.getAvailableQuantity(item, false),
				this.CartState.getTotalQuantityFromCart(item),
				item
			);
			this.CartItemsState.addToCurrentCart(item);
		} else {
			this.CartItemsState.deleteFromCart(item);
		}
	}

	onBlurHandler(item) {
		const index = getIndex(
			this.CartItemsState.cartUpdates,
			'number',
			item.number
		);

		if (
			index >= 0 &&
			item.is_configurable &&
			item.quantity_ordered &&
			!item.errorMsgId
		) {
			this.toggleModal(item);
		}
	}

	@action
	handleAddToCart() {
		this.showModal = false;

		if (this.CartItemsState.isCurrentCartEmpty) {
			this.CartItemsState.toggleWarningMessage();
		} else {
			this.CartState.addToCart(this.CartItemsState.cartUpdates).then(() => {
				this.CartItemsState.emptyCurrentCart();

				this.SearchState.search({
					query: this.SearchState.query,
					pagination: this.pagination,
					sorting: this.order,
					clearCache: true,
				});
			});
		}
	}

	@action
	onPaginationChange(pagination) {
		this.pagination = pagination;
	}

	@action
	onOrderChange(order) {
		this.order = order;
	}

	@action
	toggleModal(itemDetails) {
		this.itemDetails = itemDetails;
		this.SearchState.search({
			query: this.SearchState.query,
			pagination: this.pagination,
			sorting: this.order,
		});
		this.showModal = !this.showModal;
	}

	render() {
		const { search, query } = this.SearchState;

		return (
			<div className="ItemSearch">
				{!query && <Redirect to="/" />}
				{search.handle({
					loading: () => <Loader />,
					rejected: error =>
						<div>
							<PageHeader
								headerText={
									<span>
										<FormattedMessage id="itemSearch.genericHeaderText" />
									</span>
								}
							/>
							{error.status === INTERNAL_ERROR || error.status === BAD_REQUEST
								? <ErrorLoadingContent />
								: <InfoBox type="danger">
										<FormattedMessage id={error.message} />
									</InfoBox>}
						</div>,
					resolved: results =>
						<div>
							<PageHeader
								headerText={
									<span>
										<FormattedMessage id="itemSearch.headerText" />
										<strong>
											{query}
										</strong>
									</span>
								}
							/>
							{results.data.length
								? <div className="ItemSearch__content">
										<ItemContentHeader
											handleAddToCart={this.handleAddToCart.bind(this)}
											disableAddToCart={
												this.CartItemsState.hasError ||
												this.CartState.addToCart.state.loading ||
												results.data[0].is_preview
											}
										>
											<div className="col-md-6 ">
												<p id="ItemSearch-count">
													{results.count}{' '}
													<FormattedMessage id="itemSearch.itemsIn" />
												</p>
											</div>
										</ItemContentHeader>

										<DataTable
											id="ItemSearch-results-table"
											columns={getColumns(
												this.toggleModal.bind(this),
												this.onChangeQty.bind(this),
												this.onBlurHandler.bind(this)
											)}
											rows={results.data}
											order={this.order}
											onOrderChange={this.onOrderChange.bind(this)}
											count={results.count}
											pagination={this.pagination}
											onPaginationChange={this.onPaginationChange.bind(this)}
										/>

										{this.showModal &&
											<ChooseColor
												show={this.showModal}
												onRequestClose={this.toggleModal.bind(this)}
												item={this.itemDetails}
												handleAddToCart={this.handleAddToCart.bind(this)}
											/>}
									</div>
								: <InfoBox type="info">
										<FormattedMessage id="itemSearch.noItemsFound" />
									</InfoBox>}
						</div>,
				})}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/ItemSearch/index.js